import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  ChatSupportStatus,
  IAssignedChatModel,
  IChatMessage,
  ISupportInfo,
} from "types/Chat";

/**
 * CLE-3043 — the pure rules behind escalating a staff chat to Clear Health support and the live
 * takeover that follows. Kept out of ChatWidget.tsx so each one is unit-tested directly.
 */

/** The longest page URL the widget sends with a staff message; the API stores at most this much too. */
export const MAX_CHAT_PAGE_URL_LENGTH = 500;

/**
 * The page a staff message is sent from (`data.pageUrl`): origin + path only, cut to 500 characters. The query
 * string and hash never leave the browser — the Estimates list keeps its patient-name filter there
 * (`?patientName=…`), and the page URL is printed in the #support Slack post. An unparseable value sends "".
 */
export const currentPageUrl = (href: string = window.location.href): string => {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`.slice(0, MAX_CHAT_PAGE_URL_LENGTH);
  } catch {
    return "";
  }
};

/**
 * CLE-3043 — the staff widget's auth headers: the Auth0 bearer and the account the widget chats as
 * (its `accountId` prop — never Redux's `currentAccount`, which the widget no longer changes).
 */
export const buildStaffHeaders = (
  token: string | null | undefined,
  account: string | null | undefined
): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.authorization = `Bearer ${token}`;
  if (account) headers.account = account;
  return headers;
};

/**
 * The headers of every chat request the widget sends (list, send, escalate, support poll). The staff
 * bot: buildStaffHeaders with the widget's own account. The patient bot: the public order token only.
 */
export const buildChatRequestHeaders = (
  chatType: "PatientChatBot" | "ContractManagementBot",
  auth: {
    token: string | null | undefined;
    publicToken: string | null | undefined;
  },
  accountId: string | null | undefined
): Record<string, string> => {
  if (chatType === "ContractManagementBot") {
    return buildStaffHeaders(auth.token, accountId);
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (auth.publicToken) headers.authorization = `Bearer ${auth.publicToken}`;
  return headers;
};

/** WAITING or HUMAN: a person from Clear Health is (or is about to be) on the other end, not the bot. */
export const isSupportActive = (
  supportStatus: ChatSupportStatus | null | undefined
): boolean => supportStatus === "WAITING" || supportStatus === "HUMAN";

/**
 * The global rollout gate. A SuperAdmin always sees the staff widget. Anyone else sees it only once
 * GET /chats/assigned-model has answered AND said `staffAccessEnabled: true` — no answer yet, a failed
 * answer and an answer from an API that does not send the flag all keep it hidden (fail closed), because
 * the API refuses their messages until the switch is on.
 */
export const shouldShowStaffChatWidget = (
  isSuperAdmin: boolean,
  assignedModel:
    | Pick<IAssignedChatModel, "staffAccessEnabled">
    | null
    | undefined
): boolean => {
  if (isSuperAdmin) return true;
  return assignedModel?.staffAccessEnabled === true;
};

/** Poll GET /chats/:id only while the widget is open on a chat that support is involved in. */
export const shouldPollSupportChat = ({
  isOpen,
  chatId,
  supportStatus,
}: {
  isOpen: boolean;
  chatId: string | null | undefined;
  supportStatus: ChatSupportStatus | null | undefined;
}): boolean => isOpen && !!chatId && isSupportActive(supportStatus);

export type ChatMessageKind = "user" | "bot" | "support" | "system";

/** How one stored message renders: the author tag wins over the role. */
export const getMessageKind = (
  message: Pick<IChatMessage, "role" | "author">
): ChatMessageKind => {
  if (message.author === "system") return "system";
  if (message.author === "support") return "support";
  return message.role === "user" ? "user" : "bot";
};

const messageKey = (message: IChatMessage): string =>
  `${message.timestamp}|${message.role}|${message.content}`;

const sameText = (a: IChatMessage, b: IChatMessage): boolean =>
  a.role === b.role &&
  a.content === b.content &&
  (a.author ?? null) === (b.author ?? null);

/**
 * Merge what the server holds into what the widget shows. The server's list is the truth and comes
 * first, in its order. A local message is kept only when the server does not have it yet:
 *
 * - same (timestamp, role, content) as a server message → it IS that message, dropped;
 * - otherwise, a server message with the same role/author/content that no other local message has
 *   already claimed → it is the server's copy of an optimistic message (the widget stamps its own
 *   time when it appends what the user typed, the API stamps another), dropped;
 * - anything left is still in flight, or a notice that only exists locally — kept, after the server's.
 */
export const mergeChatMessages = (
  local: IChatMessage[],
  server: IChatMessage[] | null | undefined
): IChatMessage[] => {
  const serverList = Array.isArray(server) ? server : [];
  const serverKeys = new Set(serverList.map(messageKey));
  const localKeys = new Set(local.map(messageKey));
  // Server messages a local one could be an optimistic copy of: not matched exactly by any local one.
  const claimable = serverList.filter(
    (message) => !localKeys.has(messageKey(message))
  );

  const pending = local.filter((message) => {
    if (serverKeys.has(messageKey(message))) return false;
    const index = claimable.findIndex((candidate) =>
      sameText(candidate, message)
    );
    if (index >= 0) {
      claimable.splice(index, 1);
      return false;
    }
    return true;
  });

  return [...serverList, ...pending];
};

/**
 * Take back the optimistic bubble the widget appended for a message the API then refused (a 409: the
 * chat was ended). Removes the last message with the same (timestamp, role, content), so nothing reads
 * as "sent" that the server never stored; the caller puts the text back in the input.
 */
export const withoutOptimisticMessage = (
  messages: IChatMessage[],
  optimistic: IChatMessage
): IChatMessage[] => {
  const key = messageKey(optimistic);
  const index = messages.map(messageKey).lastIndexOf(key);
  if (index < 0) return messages;
  return [...messages.slice(0, index), ...messages.slice(index + 1)];
};

export type SupportBannerTone = "waiting" | "human" | "closed";

export interface ISupportBanner {
  tone: SupportBannerTone;
  text: string;
}

const youAt = (userEmail?: string | null): string =>
  userEmail ? ` at ${userEmail}` : "";

/**
 * The status line above the input. Null when the bot is answering (NONE). The hours are whatever the
 * API sent — never hard-coded here.
 */
export const getSupportBanner = ({
  supportStatus,
  support,
  supportAgentName,
  escalatedAt,
  now,
  userEmail,
}: {
  supportStatus: ChatSupportStatus | null | undefined;
  support: ISupportInfo | null | undefined;
  supportAgentName: string | null | undefined;
  escalatedAt: string | null | undefined;
  now: number;
  userEmail: string | null | undefined;
}): ISupportBanner | null => {
  switch (supportStatus) {
    case "WAITING": {
      if (!support) {
        return {
          tone: "waiting",
          text: "Sent to Clear Health support. Someone will join this chat shortly.",
        };
      }
      if (!support.isOnline) {
        return {
          tone: "waiting",
          text: `Sent to Clear Health support. Support is offline right now (${
            support.hoursLabel
          }) — they will email or call you${youAt(userEmail)}.`,
        };
      }
      const escalatedMs = escalatedAt ? new Date(escalatedAt).getTime() : NaN;
      const pickupMs = support.pickupMinutes * 60 * 1000;
      if (Number.isFinite(escalatedMs) && now - escalatedMs >= pickupMs) {
        return {
          tone: "waiting",
          text: "Sorry, nobody has joined yet. Clear Health support has been notified and will answer here as soon as they can.",
        };
      }
      return {
        tone: "waiting",
        text: `Sent to Clear Health support. Someone will join this chat shortly. Support hours: ${support.hoursLabel}.`,
      };
    }
    case "HUMAN":
      return {
        tone: "human",
        text: `${
          supportAgentName?.trim() || "Someone"
        } from Clear Health is in this chat.`,
      };
    case "CLOSED":
      return { tone: "closed", text: "This chat has ended." };
    default:
      return null;
  }
};

/**
 * 2026-10-29 — which chat the staff widget re-opens on its first restore after mounting (a reload, a
 * bookmark, a new tab). The staff widget has no chat-history list, so without this a chat that
 * Clear Health support is typing into would be unreachable after a reload.
 *
 * The last chat this user had open, if it is still in their list and has not ended (CLOSED); else
 * the most recently updated chat support is on (WAITING or HUMAN); else none. A CLOSED chat is never
 * restored, and a bot-only (NONE) one only as the last chat — otherwise the widget starts a new
 * chat, as before. A `?chatId=` in the URL wins over all of this; the caller checks it first.
 */
export const pickChatToRestore = <
  T extends {
    id: string;
    supportStatus?: ChatSupportStatus | null;
    updatedAt?: string | null;
  }
>(
  chats: T[],
  lastSelectedChatId: string | null | undefined
): T | null => {
  if (lastSelectedChatId) {
    const last = chats.find((chat) => chat.id === lastSelectedChatId);
    if (last && last.supportStatus !== "CLOSED") return last;
  }

  const updatedAtOf = (chat: T): number => {
    const time = chat.updatedAt ? Date.parse(chat.updatedAt) : NaN;
    return Number.isNaN(time) ? 0 : time;
  };

  return chats
    .filter((chat) => isSupportActive(chat.supportStatus))
    .reduce<T | null>(
      (newest, chat) =>
        !newest || updatedAtOf(chat) > updatedAtOf(newest) ? chat : newest,
      null
    );
};

/**
 * Whether the stored last chat id should be forgotten once this user's chat list has loaded: it is
 * no longer in the list (deleted, or not this user's), or its chat has ended (CLOSED) and so will
 * never be restored. Clearing it keeps storage from holding ids that can only ever be skipped.
 */
export const isStoredChatStale = (
  chats: { id: string; supportStatus?: ChatSupportStatus | null }[],
  storedChatId: string | null | undefined
): boolean => {
  if (!storedChatId) return false;
  const stored = chats.find((chat) => chat.id === storedChatId);
  return !stored || stored.supportStatus === "CLOSED";
};

/** The part of the Storage API the staff widget uses, so the persistence rules test with a fake. */
export interface IChatIdStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

/** Per account: a user who works in two hospitals keeps one last chat in each. */
export const staffChatStorageKey = (accountId: string): string =>
  `staffChat.lastChatId.${accountId}`;

/** `window.localStorage`, or null where reading it throws (blocked site data, some private windows). */
export const browserChatIdStorage = (): IChatIdStorage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The staff widget's last chat id for this account, or null. Never throws. */
export const readStoredChatId = (
  storage: IChatIdStorage | null | undefined,
  accountId: string | null | undefined
): string | null => {
  if (!storage || !accountId) return null;
  try {
    return storage.getItem(staffChatStorageKey(accountId)) || null;
  } catch {
    return null;
  }
};

/** Stores the staff widget's last chat id for this account; null removes it. Never throws. */
export const writeStoredChatId = (
  storage: IChatIdStorage | null | undefined,
  accountId: string | null | undefined,
  chatId: string | null
): void => {
  if (!storage || !accountId) return;
  try {
    const key = staffChatStorageKey(accountId);
    if (chatId) {
      storage.setItem(key, chatId);
    } else {
      storage.removeItem(key);
    }
  } catch {
    // Storage full or blocked: the widget still works, it just will not restore after a reload.
  }
};

/**
 * 2026-10-29 — the staff widget's last chat id, kept in storage per account. The id in state always
 * belongs to ONE account: the one it was read for, or selected under. When `accountId` changes
 * without a remount, the hook re-reads the new account's stored id and calls `onAccountChange`, and
 * it never writes an id under an account it was not read or selected for. Disabled (the patient
 * widget), it starts at null and never touches storage.
 */
export const useStoredStaffChatId = (
  enabled: boolean,
  accountId: string,
  onAccountChange?: () => void,
  getStorage: () => IChatIdStorage | null = browserChatIdStorage
): [string | null, Dispatch<SetStateAction<string | null>>] => {
  const [chatId, setChatId] = useState<string | null>(() =>
    enabled ? readStoredChatId(getStorage(), accountId) : null
  );
  // The account `chatId` was read or selected for.
  const ownerAccountRef = useRef(accountId);

  useEffect(() => {
    if (!enabled) return;
    if (ownerAccountRef.current !== accountId) {
      // The id in state is the previous account's: write nothing, read this account's instead.
      ownerAccountRef.current = accountId;
      onAccountChange?.();
      setChatId(readStoredChatId(getStorage(), accountId));
      return;
    }
    writeStoredChatId(getStorage(), accountId, chatId);
  }, [enabled, accountId, chatId]);

  return [chatId, setChatId];
};
